import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import {
  clearAuthStorage,
  getAccessToken,
  getRefreshToken,
  getStoredSession,
  setStoredSession,
  setTokens,
} from "@/shared/infrastructure/auth/token-storage";
import type { AuthSession } from "../../domain/entities";
import type {
  InitiateRegistrationInput,
  LoginCredentials,
  OnboardingInput,
} from "../../application/ports/auth.repository.port";
import {
  useDoctorLoginMutation,
  useForgotPasswordMutation,
  useGoogleLoginMutation,
  useInitiateRegistrationMutation,
  useLoginMutation,
  useLogoutMutation,
  useOnboardingMutation,
  useResetPasswordMutation,
  useSetPasswordMutation,
  useVerifyEmailMutation,
} from "../queries/auth.queries";
import { usersHttpRepository } from "@/modules/users/infrastructure/http/users.http-repository";

interface AuthContextValue {
  session: AuthSession | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  loginWithGoogle?: (idToken: string) => Promise<void>;
  doctorLogin: (credentials: LoginCredentials) => Promise<void>;
  onboarding: (input: OnboardingInput) => Promise<void>;
  logout: () => Promise<void>;
  initiateRegistration: (input: InitiateRegistrationInput) => Promise<void>;
  verifyEmail: (token: string) => Promise<void>;
  setPassword: (password: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (token: string, newPassword: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function readStoredSession(): AuthSession | null {
  return getStoredSession() as AuthSession | null;
}

function persistSession(session: AuthSession) {
  setStoredSession(session);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<AuthSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const loginMutation = useLoginMutation();
  const googleLoginMutation = useGoogleLoginMutation();
  const doctorLoginMutation = useDoctorLoginMutation();
  const onboardingMutation = useOnboardingMutation();
  const logoutMutation = useLogoutMutation();
  const initiateRegistrationMutation = useInitiateRegistrationMutation();
  const verifyEmailMutation = useVerifyEmailMutation();
  const setPasswordMutation = useSetPasswordMutation();
  const forgotPasswordMutation = useForgotPasswordMutation();
  const resetPasswordMutation = useResetPasswordMutation();

  useEffect(() => {
    if (getAccessToken()) {
      const restored = readStoredSession();
      if (restored) {
        setSession(restored);
      } else {
        clearAuthStorage();
      }
    }
    setIsLoading(false);
  }, []);

  const loginWithGoogle = async (idToken: string) => {
    const result = await googleLoginMutation.mutateAsync(idToken);
    setTokens(result);
    try {
      const profile = await usersHttpRepository.getById(result.userId);
      const nextSession: AuthSession = {
        userId: result.userId,
        fullName: result.fullName,
        email: result.email,
        role: profile.role,
      };
      persistSession(nextSession);
      setSession(nextSession);
    } catch (error) {
      clearAuthStorage();
      throw error;
    }
  };


  const login = async (credentials: LoginCredentials) => {
    const result = await loginMutation.mutateAsync(credentials);
    setTokens(result);
    try {
      // POST /auth/login não retorna o role real (sempre "patient" no repositório) — busca o
      // perfil para refletir corretamente contas admin, já que o guard de UI depende disso.
      const profile = await usersHttpRepository.getById(result.userId);
      const nextSession: AuthSession = {
        userId: result.userId,
        fullName: result.fullName,
        email: result.email,
        role: profile.role,
      };
      persistSession(nextSession);
      setSession(nextSession);
    } catch (error) {
      clearAuthStorage();
      throw error;
    }
  };

  const doctorLogin = async (credentials: LoginCredentials) => {
    const result = await doctorLoginMutation.mutateAsync(credentials);
    setTokens(result);
    const nextSession: AuthSession = {
      userId: result.userId,
      fullName: result.fullName,
      email: result.email,
      role: result.role,
    };
    persistSession(nextSession);
    setSession(nextSession);
  };

  const onboarding = async (input: OnboardingInput) => {
    const result = await onboardingMutation.mutateAsync(input);
    setTokens(result);
    const nextSession: AuthSession = {
      userId: result.userId,
      fullName: result.fullName,
      email: result.email,
      role: result.role,
    };
    persistSession(nextSession);
    setSession(nextSession);
  };

  const initiateRegistration = async (input: InitiateRegistrationInput) => {
    await initiateRegistrationMutation.mutateAsync(input);
  };

  // Autentica com um par de tokens de sessão curta — permite chamar /auth/set-password
  // (protegido) na etapa seguinte, mas ainda não é uma conta com senha definida.
  const verifyEmail = async (token: string) => {
    const result = await verifyEmailMutation.mutateAsync(token);
    setTokens(result);
    const nextSession: AuthSession = {
      userId: result.userId,
      fullName: result.fullName,
      email: result.email,
      role: result.role,
    };
    persistSession(nextSession);
    setSession(nextSession);
  };

  const setPassword = async (password: string) => {
    await setPasswordMutation.mutateAsync(password);
  };

  const forgotPassword = async (email: string) => {
    await forgotPasswordMutation.mutateAsync(email);
  };

  const resetPassword = async (token: string, newPassword: string) => {
    await resetPasswordMutation.mutateAsync({ token, newPassword });
  };

  const logout = async () => {
    const refreshToken = getRefreshToken();
    try {
      if (refreshToken) await logoutMutation.mutateAsync(refreshToken);
    } finally {
      clearAuthStorage();
      setSession(null);
      window.location.href = import.meta.env.VITE_INSTITUTIONAL_BASE_URL || "/";
    }
  };

  return (
    <AuthContext.Provider
      value={{
        session,
        isAuthenticated: !!session,
        isLoading,
        login,
        loginWithGoogle,
        doctorLogin,
        onboarding,
        logout,
        initiateRegistration,
        verifyEmail,
        setPassword,
        forgotPassword,
        resetPassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth deve ser usado dentro de AuthProvider");
  }
  return context;
}
